/**
 * Avatar utility functions for handling the two-column avatar system
 * - manual_avatar_url: Manually uploaded images (highest priority)
 * - google_avatar_url: Google OAuth profile images (fallback)
 */

export interface AvatarData {
	manualAvatarUrl?: string | null;
	googleAvatarUrl?: string | null;
	avatarSource?: "google" | "manual" | "none" | null;
}

/**
 * Get the display avatar URL with priority logic
 * Priority: manual > google > null
 */
export function getDisplayAvatarUrl(avatarData: AvatarData): string | null {
	const manualUrl = avatarData?.manualAvatarUrl;
	const googleUrl = avatarData?.googleAvatarUrl;
	const source = avatarData?.avatarSource;

	// User explicitly chose no avatar (DiceBear initials)
	if (source === "none") {
		return null;
	}

	// User explicitly chose a source — honor it if the URL exists
	if (source === "google" && googleUrl) {
		return googleUrl;
	}
	if (source === "manual" && manualUrl) {
		return manualUrl;
	}

	// Default priority: manual > google
	return manualUrl || googleUrl || null;
}

/**
 * Check if user has a manually uploaded avatar
 */
export function hasManualAvatar(avatarData: AvatarData): boolean {
	return Boolean(avatarData.manualAvatarUrl);
}

/**
 * Check if user has a Google avatar
 */
export function hasGoogleAvatar(avatarData: AvatarData): boolean {
	return Boolean(avatarData.googleAvatarUrl);
}

/**
 * Check if user has any avatar
 */
export function hasAnyAvatar(avatarData: AvatarData): boolean {
	return getDisplayAvatarUrl(avatarData) !== null;
}

/**
 * Get avatar source information
 */
export function getAvatarSource(
	avatarData: AvatarData,
): "manual" | "google" | "none" {
	// If user explicitly chose a source, honor it
	if (avatarData.avatarSource === "none") {
		return "none";
	}
	if (avatarData.avatarSource === "google" && avatarData.googleAvatarUrl) {
		return "google";
	}
	if (avatarData.avatarSource === "manual" && avatarData.manualAvatarUrl) {
		return "manual";
	}

	// Default: derive from available URLs
	if (avatarData.manualAvatarUrl) {
		return "manual";
	}
	if (avatarData.googleAvatarUrl) {
		return "google";
	}
	return "none";
}

/**
 * Determines if an avatar URL is a manual upload (base64 data URL)
 */
export function isManualUpload(avatarUrl: string): boolean {
	if (!avatarUrl || typeof avatarUrl !== "string") return false;
	return avatarUrl.startsWith("data:image/");
}

/**
 * Determines if an avatar URL is a Google-cached image (Supabase Storage URL)
 * Note: This requires NEXT_PUBLIC_SUPABASE_URL or EXPO_PUBLIC_SUPABASE_URL env var
 */
export function isGoogleCachedAvatar(avatarUrl: string): boolean {
	if (!avatarUrl || typeof avatarUrl !== "string") return false;

	// Check for both Next.js and Expo env vars
	const supabaseUrl = 
		typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_URL ||
		typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_SUPABASE_URL;
		
	return supabaseUrl ? avatarUrl.includes(supabaseUrl) : false;
}